import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { CommissionBasisSchema, CommissionStatementSchema } from './buyer-commission';
import { WebExposeViewSchema } from './web-expose-view.schemas';

export const PORTALS = ['IMMOSCOUT24', 'KLEINANZEIGEN', 'IMMOWELT'] as const;
export const OPENIMMO_PORTALS = ['KLEINANZEIGEN', 'IMMOWELT'] as const;
export const PORTAL_CONNECTION_STATUSES = ['NOT_CONNECTED', 'CONNECTED', 'EXPIRED'] as const;

export type Portal = (typeof PORTALS)[number];
export type OpenImmoPortal = (typeof OPENIMMO_PORTALS)[number];

/** The URL segment of each OpenImmo portal; ImmoScout24 keeps its own static leaves. */
export const OPENIMMO_PORTAL_SLUGS = { KLEINANZEIGEN: 'kleinanzeigen', IMMOWELT: 'immowelt' } as const satisfies Record<
  OpenImmoPortal,
  string
>;
export const OpenImmoPortalSlugSchema = z.enum(['kleinanzeigen', 'immowelt']);
export const openImmoPortalOf = (slug: z.infer<typeof OpenImmoPortalSlugSchema>): OpenImmoPortal =>
  slug === 'kleinanzeigen' ? 'KLEINANZEIGEN' : 'IMMOWELT';
export type PortalConnectionStatus = (typeof PORTAL_CONNECTION_STATUSES)[number];

export const PortalConnectionPathParamsSchema = z.object({ workspaceId: z.uuid() });
export const PortalAccessPathParamsSchema = z.object({ workspaceId: z.uuid(), portal: OpenImmoPortalSlugSchema });

export const PortalAccessViewSchema = z.object({
  host: z.string(),
  username: z.string(),
  providerId: z.string(),
  listingEmail: z.string(),
});
export type PortalAccessView = z.infer<typeof PortalAccessViewSchema>;

export const PortalConnectionResponseSchema = z.object({
  portal: z.enum(PORTALS),
  available: z.boolean(),
  status: z.enum(PORTAL_CONNECTION_STATUSES),
  accountName: z.string().nullable(),
  connectedBy: z.object({ memberId: refersTo('member', z.uuid()), displayName: z.string() }).nullable(),
  connectedAt: z.iso.datetime().nullable(),
  /** OpenImmo portals with a grant only; never the password. `null` for ImmoScout24 and for NOT_CONNECTED. */
  access: PortalAccessViewSchema.nullable(),
});
export type PortalConnectionResponse = z.infer<typeof PortalConnectionResponseSchema>;

export const ListPortalConnectionsResponseSchema = z.object({
  /** One entry per portal, always all three, in `PORTALS` order. */
  connections: z.array(PortalConnectionResponseSchema),
});
export type ListPortalConnectionsResponse = z.infer<typeof ListPortalConnectionsResponseSchema>;

const ftpHost = z
  .string()
  .trim()
  .transform((value) => value.replace(/^ftp:\/\//i, '').replace(/\/+$/, ''))
  .pipe(
    z
      .string()
      .min(1)
      .max(253)
      .regex(/^[^\s/:]+$/),
  );

export const SavePortalAccessBodySchema = z
  .object({
    host: ftpHost,
    /** „Spaces removed": every whitespace character, not only the ends (PRD Fields). */
    username: z
      .string()
      .transform((value) => value.replace(/\s+/g, ''))
      .pipe(z.string().min(1).max(128)),
    /** Never trimmed: a password is taken as typed. Absent = keep the stored one (only `listingEmail` may then change). */
    password: z.string().min(1).max(256).optional(),
    providerId: z.string().trim().min(1).max(64),
    listingEmail: z.string().trim().pipe(z.email().max(254)),
  })
  .strict();
export type SavePortalAccessBody = z.infer<typeof SavePortalAccessBodySchema>;

export const AdminPortalConnectionResponseSchema = z.object({
  portal: z.enum(PORTALS),
  status: z.enum(PORTAL_CONNECTION_STATUSES),
  accountName: z.string().nullable(),
  connectedBy: z
    .object({ memberId: refersTo('member', z.uuid()), displayName: z.string(), email: z.string() })
    .nullable(),
  connectedAt: z.iso.datetime().nullable(),
  expiredAt: z.iso.datetime().nullable(),
  lastError: z.object({ message: z.string(), at: z.iso.datetime() }).nullable(),
});
export type AdminPortalConnectionResponse = z.infer<typeof AdminPortalConnectionResponseSchema>;

export const AdminPortalConnectionsResponseSchema = z.object({
  /** One entry per portal, always all three, in `PORTALS` order. */
  connections: z.array(AdminPortalConnectionResponseSchema),
});
export type AdminPortalConnectionsResponse = z.infer<typeof AdminPortalConnectionsResponseSchema>;

export const StartPortalConnectionResponseSchema = z.object({ authorizationUrl: z.url() });
export type StartPortalConnectionResponse = z.infer<typeof StartPortalConnectionResponseSchema>;

export const CompletePortalConnectionBodySchema = z
  .object({
    state: z.string().min(16).max(128),
    oauthToken: z.string().min(1).max(512),
    oauthVerifier: z.string().min(1).max(512).nullable(),
  })
  .strict();
export type CompletePortalConnectionBody = z.infer<typeof CompletePortalConnectionBodySchema>;

export const IMMOSCOUT24_LISTING_TYPES = [
  'APARTMENT_BUY',
  'APARTMENT_RENT',
  'HOUSE_BUY',
  'HOUSE_RENT',
  'LIVING_BUY_SITE',
  'LIVING_RENT_SITE',
  'TRADE_SITE',
  'GARAGE_BUY',
  'GARAGE_RENT',
  'OFFICE',
  'STORE',
  'GASTRONOMY',
  'INDUSTRY',
  'SPECIAL_PURPOSE',
  'INVESTMENT',
] as const;
export type Immoscout24ListingType = (typeof IMMOSCOUT24_LISTING_TYPES)[number];

export const isImmoscout24ListingType = (value: string): value is Immoscout24ListingType =>
  (IMMOSCOUT24_LISTING_TYPES as ReadonlyArray<string>).includes(value);

export const PORTAL_LISTING_TYPES = [...IMMOSCOUT24_LISTING_TYPES, 'OPENIMMO'] as const;
export type PortalListingType = (typeof PORTAL_LISTING_TYPES)[number];

export const PORTAL_READINESS_ITEMS = [
  'NOT_ACTIVE',
  'NOT_CONNECTED',
  'CONNECTION_EXPIRED',
  'SUBTYPE_MISSING',
  'CONTACT_INCOMPLETE',
  'COMMERCIAL_FINE_TYPE',
  'STREET',
  'HOUSE_NUMBER',
  'POSTAL_CODE',
  'CITY',
  'HOUSE_NUMBER_TOO_LONG',
  'CITY_TOO_LONG',
  'TITLE_TOO_LONG',
  'DESCRIPTION_TOO_LONG',
  'EQUIPMENT_DESCRIPTION_TOO_LONG',
  'LOCATION_DESCRIPTION_TOO_LONG',
  'MISCELLANEOUS_TOO_LONG',
  'LIVING_AREA',
  'ROOMS',
  'PLOT_AREA',
  'NET_FLOOR_SPACE',
  'TOTAL_FLOOR_SPACE',
  'PRICE',
  'RENTAL_COMMISSION',
  'RENTAL_COMMISSION_AMOUNT',
  'BUYER_COMMISSION_MISSING',
  'COMMISSION_REENTRY_REQUIRED',
  'BUYER_COMMISSION_ABOVE_SELLER',
  'BUYER_COMMISSION_BELOW_SELLER',
  'COMMISSION_NOTE_TOO_LONG',
  'GEG_CERTIFICATE_BASIS',
  'GEG_ENERGY_VALUE',
  'GEG_ENERGY_SOURCE',
  'GEG_YEAR_BUILT',
  'GEG_ENERGY_CLASS',
  'GMODG_CERTIFICATE_BASIS',
  'GMODG_PRIMARY_ENERGY_VALUE',
  'GMODG_ENERGY_CLASS',
  'GMODG_YEAR_BUILT',
  'GMODG_ENERGY_SOURCE',
  'CERTIFICATE_EXPIRED',
  'PRICE_ON_INQUIRY',
  'COMPANY_DATA_INCOMPLETE',
  'CATEGORY_UNSUPPORTED',
  'PICTURES_OVER_LIMIT',
] as const;
export const PORTAL_READINESS_SEVERITIES = ['BLOCKER', 'HINT'] as const;
export const PORTAL_READINESS_SECTIONS = [
  'LIFECYCLE',
  'PORTAL_SETTINGS',
  'MEMBER_PROFILE',
  'IDENTITY',
  'ADDRESS',
  'AREAS',
  'PRICING_SALE',
  'PRICING_RENTAL',
  'DESCRIPTIONS',
  'ENERGY',
  'BUILDING',
  'COMPANY_SETTINGS',
  'MEDIA',
] as const;
export const PORTAL_CONTACT_FIELDS = ['EMAIL', 'LAST_NAME'] as const;
export const PORTAL_COMPANY_FIELDS = [
  'COMPANY_NAME',
  'LEGAL_FORM',
  'ADDRESS',
  'EMAIL',
  'PHONE',
  'MANAGING_DIRECTORS',
  'COMMERCIAL_REGISTER_COURT',
  'COMMERCIAL_REGISTER_NUMBER',
  'SUPERVISORY_AUTHORITY',
] as const;

export type PortalReadinessItemCode = (typeof PORTAL_READINESS_ITEMS)[number];
export type PortalReadinessSeverity = (typeof PORTAL_READINESS_SEVERITIES)[number];
export type PortalReadinessSection = (typeof PORTAL_READINESS_SECTIONS)[number];
export type PortalContactField = (typeof PORTAL_CONTACT_FIELDS)[number];
export type PortalCompanyField = (typeof PORTAL_COMPANY_FIELDS)[number];

export const PropertyPortalListingsPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const PropertyOpenImmoListingPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  portal: OpenImmoPortalSlugSchema,
});

export const PortalReadinessItemSchema = z.object({
  item: z.enum(PORTAL_READINESS_ITEMS),
  severity: z.enum(PORTAL_READINESS_SEVERITIES),
  listingType: z.enum(PORTAL_LISTING_TYPES).nullable(),
  section: z.enum(PORTAL_READINESS_SECTIONS),
  memberId: refersTo('member', z.uuid()).nullable(),
  memberName: z.string().nullable(),
  contactFields: z.array(z.enum(PORTAL_CONTACT_FIELDS)),
  companyFields: z.array(z.enum(PORTAL_COMPANY_FIELDS)),
  limit: z.number().int().positive().nullable(),
  validUntil: z.iso.date().nullable(),
});
export type PortalReadinessItem = z.infer<typeof PortalReadinessItemSchema>;

export const PortalReadinessResponseSchema = z.object({
  portal: z.enum(PORTALS),
  ready: z.boolean(),
  listingTypes: z.array(z.enum(PORTAL_LISTING_TYPES)),
  items: z.array(PortalReadinessItemSchema),
  // IMM-1168: the commission the listing would carry. Null on a rental that keeps its free text (IMM-1168.4).
  commission: z
    .object({
      hasCommission: z.boolean(),
      commissionBasis: CommissionBasisSchema.nullable(),
      commissionRateNet: z.number().nullable(),
      commissionAmountNet: z.number().nullable(),
      commissionNote: z.string().nullable(),
      statement: CommissionStatementSchema.nullable(),
    })
    .nullable(),
  energyParagraph: z.string().nullable(),
});
export type PortalReadinessResponse = z.infer<typeof PortalReadinessResponseSchema>;

export const PORTAL_LISTING_OPERATIONS = ['TRANSMIT', 'WITHDRAW'] as const;
export const PORTAL_LISTING_STATUSES = ['PUBLISHED', 'TRANSMITTED', 'STALE', 'FAILED', 'WITHDRAWN'] as const;
export const PORTAL_LISTING_FAILURES = ['TRANSMISSION', 'WITHDRAWAL'] as const;

export type PortalListingOperation = (typeof PORTAL_LISTING_OPERATIONS)[number];
export type PortalListingStatus = (typeof PORTAL_LISTING_STATUSES)[number];
export type PortalListingFailure = (typeof PORTAL_LISTING_FAILURES)[number];

export const PortalListingViewSchema = z.object({
  id: z.uuid(),
  portal: z.enum(PORTALS),
  listingType: z.enum(PORTAL_LISTING_TYPES),
  status: z.enum(PORTAL_LISTING_STATUSES).nullable(),
  live: z.boolean(),
  inFlight: z.enum(PORTAL_LISTING_OPERATIONS).nullable(),
  controllable: z.boolean(),
  portalListingId: z.string().nullable(),
  publicUrl: z.url().nullable(),
  contact: z.object({ memberId: refersTo('member', z.uuid()), displayName: z.string().nullable() }).nullable(),
  showAddress: z.boolean(),
  onlineSince: z.iso.datetime().nullable(),
  lastTransmittedAt: z.iso.datetime().nullable(),
  withdrawnAt: z.iso.datetime().nullable(),
  failure: z.enum(PORTAL_LISTING_FAILURES).nullable(),
  onlineDespite: z.enum(['ARCHIVED', 'INACTIVE']).nullable(),
  lastPortalMessage: z.object({ text: z.string(), at: z.iso.datetime() }).nullable(),
  transmittedMediaIds: z.array(z.uuid()),
});
export type PortalListingView = z.infer<typeof PortalListingViewSchema>;

// IMM-1406: one run of the browser extension on an Objekt's ImmoScout24 listing.
export const BROWSER_RUN_KINDS = ['SEND', 'UPDATE', 'GO_LIVE', 'TAKE_OFFLINE', 'DELETE'] as const;
// IMM-1409: the kinds that walk ScoutManager's pages instead of calling its API.
export const BROWSER_PAGE_RUN_KINDS = ['GO_LIVE', 'TAKE_OFFLINE', 'DELETE'] as const;
export const BROWSER_END_LISTING_KINDS = ['TAKE_OFFLINE', 'DELETE'] as const;
export const BROWSER_RUN_SOURCES = ['APP', 'CLIENT'] as const;
export const BROWSER_RUN_STATUSES = [
  'AWAITING_CONFIRMATION',
  'CONFIRMED',
  'RUNNING',
  'DONE',
  'FAILED',
  'DECLINED',
  'LAPSED',
] as const;
export const BROWSER_RUN_FAILURES = ['NOT_READY', 'IS24_REFUSED', 'IS24_SIGNED_OUT', 'EXTENSION_FAILED'] as const;
export const BROWSER_RUN_STALE_AFTER_MS = 10 * 60_000;

export type BrowserRunKind = (typeof BROWSER_RUN_KINDS)[number];
export type BrowserPageRunKind = (typeof BROWSER_PAGE_RUN_KINDS)[number];
export type BrowserEndListingKind = (typeof BROWSER_END_LISTING_KINDS)[number];
export type BrowserRunSource = (typeof BROWSER_RUN_SOURCES)[number];
export type BrowserRunStatus = (typeof BROWSER_RUN_STATUSES)[number];
export type BrowserRunFailure = (typeof BROWSER_RUN_FAILURES)[number];

export const PublicationOptionSchema = z
  .object({ value: z.string().min(1).max(200), label: z.string().min(1).max(200) })
  .strict();
export type PublicationOption = z.infer<typeof PublicationOptionSchema>;

export const BrowserRunViewSchema = z.object({
  id: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  kind: z.enum(BROWSER_RUN_KINDS),
  source: z.enum(BROWSER_RUN_SOURCES),
  // Effective: lapsed and stale already applied.
  status: z.enum(BROWSER_RUN_STATUSES),
  listingType: z.enum(IMMOSCOUT24_LISTING_TYPES).nullable(),
  publicationOptions: z.array(PublicationOptionSchema),
  requestedBy: z.object({ memberId: refersTo('member', z.uuid()), displayName: z.string().nullable() }),
  requestedAt: z.iso.datetime(),
  confirmedAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  progress: z.object({ done: z.number().int().min(0), total: z.number().int().min(1) }).nullable(),
  failure: z.enum(BROWSER_RUN_FAILURES).nullable(),
  // IS24_REFUSED only: IS24's text, verbatim.
  portalMessage: z.string().nullable(),
  // DONE only: the German field names IS24 did not keep.
  notKept: z.array(z.string()),
});
export type BrowserRunView = z.infer<typeof BrowserRunViewSchema>;

export const PropertyPortalListingsResponseSchema = z.object({
  connection: z.object({ status: z.enum(PORTAL_CONNECTION_STATUSES) }),
  connections: z.array(z.object({ portal: z.enum(PORTALS), status: z.enum(PORTAL_CONNECTION_STATUSES) })),
  listings: z.array(PortalListingViewSchema),
  // null unless the Objekt is for sale: only a Kauf Objekt has a web exposé.
  webExpose: WebExposeViewSchema.nullable(),
  // The Objekt's latest extension run on ImmoScout24, open or finished; null when it has none.
  immoscout24Run: BrowserRunViewSchema.nullable(),
});
export type PropertyPortalListingsResponse = z.infer<typeof PropertyPortalListingsResponseSchema>;

export const TransmitPortalListingsBodySchema = z
  .object({
    showAddress: z.boolean().optional(),
    attachPortalListingId: z
      .string()
      .trim()
      .regex(/^\d{1,20}$/)
      .optional(),
  })
  .strict();
export type TransmitPortalListingsBody = z.infer<typeof TransmitPortalListingsBodySchema>;

export const TransmitOpenImmoListingBodySchema = z.object({ showAddress: z.boolean().optional() }).strict();
export type TransmitOpenImmoListingBody = z.infer<typeof TransmitOpenImmoListingBodySchema>;
